/**
 * Dexie (IndexedDB) local store — 06 §0 "Local message cache".
 *
 * Survives reload. In production this holds ciphertext + a decrypted-in-memory
 * layer; in this backendless build we persist the already-decrypted view-model
 * (Message/Conversation) so the demo state and drafts survive refresh.
 *
 * Search (S21/S22) runs ONLY over this on-device cache (06 §1.1).
 */

import Dexie, { type Table } from 'dexie';
import type { Conversation, Message, User } from './model';

/** Row wrapper so we can index by conversation + order key. */
export interface StoredMessage extends Message {
  /** Composite-ish sort key: seq when present else sentAtMs (kept large). */
  orderKey: number;
}

export class SinyalDb extends Dexie {
  conversations!: Table<Conversation, string>;
  messages!: Table<StoredMessage, string>;
  users!: Table<User, string>;
  /** Generic KV for theme, session, etc. */
  kv!: Table<{ key: string; value: unknown }, string>;

  constructor() {
    super('sinyal');
    this.version(1).stores({
      conversations: 'id, updatedAtMs, pinned, archived',
      // primary key is clientMsgId; indexed on conversation + order.
      messages: 'clientMsgId, conversationId, [conversationId+orderKey], seq, sentAtMs',
      users: 'id, handle',
      kv: 'key',
    });
  }
}

export const db = new SinyalDb();

export function orderKeyOf(m: Message): number {
  return m.seq ?? Math.floor(m.sentAtMs);
}

export async function persistMessage(m: Message): Promise<void> {
  await db.messages.put({ ...m, orderKey: orderKeyOf(m) });
}

export async function persistConversation(c: Conversation): Promise<void> {
  await db.conversations.put(c);
}

export async function loadConversationMessages(cid: string): Promise<Message[]> {
  const rows = await db.messages
    .where('[conversationId+orderKey]')
    .between([cid, Dexie.minKey], [cid, Dexie.maxKey])
    .toArray();
  return rows.map(({ orderKey: _orderKey, ...m }) => m);
}

/** Client-side full-text search over the decrypted cache (06 §1.1). */
export async function searchMessages(query: string): Promise<Message[]> {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out: Message[] = [];
  await db.messages.each((row) => {
    if (!row.deleted && row.text.toLowerCase().includes(q)) {
      const { orderKey: _ok, ...m } = row;
      out.push(m);
    }
  });
  return out.sort((a, b) => b.sentAtMs - a.sentAtMs);
}
